import React, { useState, useEffect } from 'react';
import api from '../../api';
import { toast } from 'sonner';

const MESES_FULL = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function fmtCLP(v) {
  if (!v && v !== 0) return '—';
  return '$' + Number(v).toLocaleString('es-CL');
}

function EstadoIcono({ estado }) {
  if (estado === 'ok') return <span className="text-green-600 font-bold">✓</span>;
  if (estado === 'error') return <span className="text-red-600 font-bold">✗</span>;
  if (estado === 'pendiente') return <span className="text-amber-500 font-bold">○</span>;
  return <span className="text-gray-400">—</span>;
}

export function TabBitacora({ clienteId, anio }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/contabilidad/empresa/${clienteId}/bitacora`, { params: { anio } })
      .then(r => setData(r.data))
      .catch(() => toast.error('No se pudo cargar la bitácora'))
      .finally(() => setLoading(false));
  }, [clienteId, anio]);

  if (loading) return <p className="text-sm text-gray-400 py-8 text-center">Cargando bitácora...</p>;
  if (!data?.meses) return null;

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Cumplimiento mensual — descarga SII, Inputsoft, boletas, centralización y F29.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {data.meses.map(m => (
          <div key={m.mes} className={`bg-white border rounded-xl p-4 shadow-sm ${m.soloBoletas ? 'border-indigo-200 bg-indigo-50/30' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-800">{MESES_FULL[m.mes]} {anio}</h4>
              {m.soloBoletas && (
                <span className="text-[10px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                  Solo boletas
                </span>
              )}
            </div>
            <ul className="space-y-1">
              {m.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                  <EstadoIcono estado={item.estado} />
                  <span className="flex-1">{item.label}</span>
                </li>
              ))}
            </ul>
            {itemDetalle(m)}
          </div>
        ))}
      </div>
    </div>
  );
}

function itemDetalle(m) {
  const f29 = m.items.find(i => i.label === 'F29');
  if (f29?.estado === 'pendiente') {
    return <p className="text-[10px] text-amber-600 mt-2">{f29.detalle}</p>;
  }
  return null;
}

export function TabAlertas({ clienteId, anio }) {
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/contabilidad/empresa/${clienteId}/watchlist`, { params: { anio } })
      .then(r => setAlertas(r.data.alertas || []))
      .catch(() => toast.error('No se pudo cargar alertas'))
      .finally(() => setLoading(false));
  }, [clienteId, anio]);

  if (loading) return <p className="text-sm text-gray-400 py-8 text-center">Analizando inconsistencias...</p>;

  if (alertas.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
        <p className="text-green-700 font-medium">Sin alertas de inconsistencia SII ↔ Inputsoft</p>
        <p className="text-xs text-green-600 mt-1">No se detectaron rechazos cruzados en {anio}.</p>
      </div>
    );
  }

  const estilosSeveridad = {
    alta: { card: 'border-red-200', badge: 'bg-red-100 text-red-800' },
    media: { card: 'border-amber-200', badge: 'bg-amber-100 text-amber-800' },
    info: { card: 'border-blue-200', badge: 'bg-blue-100 text-blue-800' },
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Watchlist: desajustes entre lo descargado del SII y lo procesado en Inputsoft.</p>
      {alertas.map((a, i) => {
        const est = estilosSeveridad[a.severidad] || { card: 'border-gray-200', badge: 'bg-gray-100 text-gray-800' };
        return (
        <div key={i} className={`border rounded-xl p-4 bg-white shadow-sm ${est.card}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${est.badge}`}>
              {a.severidad}
            </span>
            <span className="text-sm font-semibold text-gray-800">{a.titulo}</span>
          </div>
          <p className="text-xs text-gray-600">{a.mensaje}</p>
          {a.detalle && <p className="text-[10px] text-gray-400 mt-1 font-mono truncate">{a.detalle}</p>}
          <p className="text-[10px] text-gray-400 mt-1">{a.tipoDocumento} — mes {a.mes}/{a.anio}</p>
        </div>
        );
      })}
    </div>
  );
}

export function TabLibroDTE({ clienteId, anio, mesSeleccionado }) {
  const [mes, setMes] = useState(mesSeleccionado || new Date().getMonth() + 1);
  const [tipo, setTipo] = useState('ventas');
  const [data, setData] = useState(null);
  const [dteExpandido, setDteExpandido] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mesSeleccionado) setMes(mesSeleccionado);
  }, [mesSeleccionado]);

  useEffect(() => {
    setLoading(true);
    api.get(`/contabilidad/empresa/${clienteId}/libro-dte`, { params: { mes, anio, tipo } })
      .then(r => setData(r.data))
      .catch(() => toast.error('No se pudo cargar libro DTE'))
      .finally(() => setLoading(false));
  }, [clienteId, anio, mes, tipo]);

  const MESES_SHORT = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1">
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
            <button key={m} onClick={() => setMes(m)}
              className={`w-9 h-9 rounded-lg text-xs font-bold border transition-colors ${
                mes === m ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-indigo-50'
              }`}>
              {MESES_SHORT[m]}
            </button>
          ))}
        </div>
        <span className="text-sm font-semibold text-gray-700">{MESES_FULL[mes]} {anio}</span>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {['ventas', 'compras'].map(t => (
            <button key={t} onClick={() => setTipo(t)}
              className={`px-3 py-1 text-xs font-medium rounded-md capitalize ${tipo === t ? 'bg-white shadow text-indigo-700' : 'text-gray-500'}`}>
              {t}
            </button>
          ))}
        </div>
        {data?.soloBoletas && (
          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-medium">
            📋 Mes solo boletas — sin facturas detalladas en CSV
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 text-center py-8">Cargando resumen DTE...</p>
      ) : !data?.resumen?.length ? (
        <p className="text-sm text-gray-400 text-center py-8">Sin datos de libro {tipo} para este mes.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <div className="bg-white border rounded-lg p-3 text-center">
              <div className="text-xs text-gray-400">Tipos DTE</div>
              <div className="text-lg font-bold text-gray-800">{data.totales.tipos}</div>
            </div>
            <div className="bg-white border rounded-lg p-3 text-center">
              <div className="text-xs text-gray-400">Documentos</div>
              <div className="text-lg font-bold text-gray-800">{data.totales.documentos}</div>
            </div>
            <div className="bg-white border rounded-lg p-3 text-center">
              <div className="text-xs text-gray-400">Total</div>
              <div className="text-lg font-bold text-indigo-700">{fmtCLP(data.totales.total)}</div>
            </div>
          </div>

          <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Cód.</th>
                  <th className="px-3 py-2 text-left">Tipo DTE</th>
                  <th className="px-3 py-2 text-right">Cant.</th>
                  <th className="px-3 py-2 text-right">Neto</th>
                  <th className="px-3 py-2 text-right">IVA</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.resumen.map(g => (
                  <React.Fragment key={g.codigo}>
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setDteExpandido(dteExpandido === g.codigo ? null : g.codigo)}>
                      <td className="px-3 py-2 font-mono font-bold">{g.codigo}</td>
                      <td className="px-3 py-2">{g.nombre}</td>
                      <td className="px-3 py-2 text-right">{g.totalDocumentos}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtCLP(g.montoNeto)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtCLP(g.montoIva)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmtCLP(g.montoTotal)}</td>
                      <td className="px-3 py-2 text-gray-400">{dteExpandido === g.codigo ? '▼' : '▶'}</td>
                    </tr>
                    {dteExpandido === g.codigo && (
                      <tr>
                        <td colSpan={7} className="px-3 py-2 bg-slate-50">
                          <p className="text-[10px] text-gray-500 mb-1">Detalle SII — {g.tipoDocumentoOriginal}</p>
                          {g.filas.map((f, j) => (
                            <div key={j} className="text-xs text-gray-600 flex gap-4 py-0.5">
                              <span>{f.tipoDocumento}</span>
                              <span>{f.totalDocumentos || 0} docs</span>
                              <span className="font-mono">{fmtCLP(f.montoTotal)}</span>
                            </div>
                          ))}
                          {data.detalle?.filas?.length > 0 && (
                            <div className="mt-2 border-t pt-2">
                              <p className="text-[10px] font-semibold text-gray-500 mb-1">Muestra CSV ({data.detalle.total} filas)</p>
                              <div className="max-h-32 overflow-y-auto">
                                {data.detalle.filas.filter(f => String(f.tipoDoc).includes(g.codigo) || g.codigo === 'OTRO').slice(0, 15).map((f, k) => (
                                  <div key={k} className="text-[10px] font-mono text-gray-500">
                                    Folio {f.folio} — {f.tipoDoc} — {fmtCLP(f.montoTotal || f.montoNeto)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export function PanelReejecucionSelectiva({ clienteId, mes, anio, tipoDocumento, onDone }) {
  const [ejecutando, setEjecutando] = useState('');

  const modos = [
    { id: 'descarga_sii', label: '↻ Solo re-descargar SII', desc: 'Descarga y procesa CSV sin subir a Inputsoft', color: 'blue' },
    { id: 'subida_inputsoft', label: '↑ Solo re-subir Inputsoft', desc: 'Usa archivos del último log — subida + boletas', color: 'emerald' },
    { id: 'centralizacion', label: '⊞ Solo re-centralizar', desc: 'Centralización en Inputsoft con datos previos', color: 'amber' },
  ];

  const ejecutar = async (modo) => {
    setEjecutando(modo);
    try {
      await api.post('/contabilidad/ejecutar-selectivo', { clienteId, mes, anio, tipoDocumento, modoSelectivo: modo });
      toast.success(`Re-ejecución ${modo} iniciada`);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al iniciar');
    } finally {
      setEjecutando('');
    }
  };

  const estilosModo = {
    descarga_sii: 'border-blue-200 hover:border-blue-300',
    subida_inputsoft: 'border-emerald-200 hover:border-emerald-300',
    centralizacion: 'border-amber-200 hover:border-amber-300',
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Re-ejecución selectiva</h4>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {modos.map(m => (
          <button key={m.id} onClick={() => ejecutar(m.id)} disabled={!!ejecutando}
            className={`text-left p-3 rounded-lg border bg-white hover:shadow-md transition-all disabled:opacity-50 ${estilosModo[m.id] || 'border-gray-200'}`}>
            <div className="text-xs font-bold text-gray-800">{m.label}</div>
            <div className="text-[10px] text-gray-500 mt-1">{m.desc}</div>
            {ejecutando === m.id && <div className="text-[10px] text-indigo-600 mt-1 animate-pulse">Iniciando...</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PanelHistorialVersiones({ clienteId, mes, anio, tipoDocumento }) {
  const [versiones, setVersiones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tipoDocumento) return;
    setLoading(true);
    api.get(`/contabilidad/empresa/${clienteId}/versiones`, { params: { mes, anio, tipoDocumento } })
      .then(r => setVersiones(r.data.versiones || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clienteId, mes, anio, tipoDocumento]);

  if (loading) return null;
  if (!versiones.length) return (
    <p className="text-xs text-gray-400 italic text-center py-2">Sin historial de re-ejecuciones para este proceso.</p>
  );

  return (
    <div className="bg-white border rounded-xl p-4 shadow-sm">
      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Historial de versiones</h4>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {versiones.map(v => (
          <div key={v._id} className="text-xs border-b border-gray-100 pb-2 last:border-0">
            <div className="flex justify-between text-gray-500">
              <span>{new Date(v.createdAt).toLocaleString('es-CL')}</span>
            </div>
            <p className="text-gray-700 font-medium mt-0.5">{v.resumenHumano || 'Comparación registrada'}</p>
            {v.diff?.montosCambiados?.length > 0 && (
              <ul className="mt-1 text-[10px] text-amber-700">
                {v.diff.montosCambiados.map((c, i) => (
                  <li key={i}>{c.tipo}: {c.docsAnterior}→{c.docsNuevo} docs, {fmtCLP(c.totalAnterior)}→{fmtCLP(c.totalNuevo)}</li>
                ))}
              </ul>
            )}
            {v.diff?.tiposNuevos?.length > 0 && (
              <p className="text-[10px] text-green-700">+{v.diff.tiposNuevos.length} tipo(s) nuevo(s)</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function BadgeSoloBoletas({ log }) {
  if (!log) return null;
  const archivo = log.descargaSii?.archivo || '';
  const mensaje = log.subidaInputsoft?.mensaje || '';
  const esSolo = archivo.includes('empty_') || /omitido.*sin facturas|solo boletas/i.test(mensaje) || log.procesamientoCsv?.filasProcesadas === 0;
  if (!esSolo || log.tipoDocumento !== 'ventas') return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
      📋 Solo boletas
    </span>
  );
}
